import { useIntl } from "react-intl";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Calibrations } from "../../../api/calibrations";
import { MutationKey } from "../../../consts/mutation-keys";
import { QueryKey } from "../../../consts/query-keys";
import { useApiSuccess } from "../../../hooks/useApiSuccess";
import { useFetchError } from "../../../hooks/useFetchError";
import { ApiError } from "../../../types/api-error";

export type UseCalibrationUploadProps = {
  datasetId: number;
  cafsId: number;
  cafsType: "plant" | "input";
  onProgress: (progress: number) => void;
  onUploadComplete: () => void;
};

export function useCalibrationUpload({
  datasetId,
  cafsId,
  cafsType,
  onProgress,
  onUploadComplete,
}: UseCalibrationUploadProps) {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const { handleSuccess } = useApiSuccess({
    snackbarText: formatMessage({
      id: "ACTION.CALIBRATION_UPLOAD_SUCCESS",
      defaultMessage: "Calibration file uploaded successfully",
      description: "Calibration upload success",
    }),
  });

  const mutationFn = (data: FormData) => {
    if (cafsType === "plant") {
      return Calibrations.uploadPlantCalibration(
        { path: { datasetId, plantId: cafsId } },
        data,
        onProgress,
        onUploadComplete,
      );
    } else {
      return Calibrations.uploadInputCalibration(
        { path: { datasetId, inputId: cafsId } },
        data,
        onProgress,
        onUploadComplete,
      );
    }
  };

  const {
    mutate: upload,
    isPending,
    isSuccess,
    error,
  } = useMutation({
    mutationKey: [MutationKey.UploadCalibration],
    mutationFn: mutationFn,
    onSuccess: () => {
      handleSuccess();
      // Invalidate calibration query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Calibration, datasetId, cafsId, cafsType],
      });
    },
  });

  useFetchError(error as unknown as ApiError);

  return { upload, isPending, isSuccess };
}
